<template>
  <div class="entity-list"
       :class="{ 'entity-list--padding': isLoading }">
    <VcLoader v-if="!infiniteScroll"/>

    <EmptyState v-if="isEmpty"
                :svg-path="emptyStateSvgPath"
                :title="emptyStateTitle"
                :subtitle="emptyStateSubtitle"
                :button-text="emptyStateCtaText"
                @cta-click="$emit('cta-click')"/>

    <NoResultsCmp v-else-if="isError"
                  icon="icon-warning"
                  :text="errorText"
                  :action-text="errorActionText"
                  @action-click="refresh"/>

    <div v-else
         class="groups-container">
      <template v-for="group in parsedGroups">
        <GroupHeader v-if="group.title"
                     :title="group.title"
                     :summary-text="group.summaryText"/>
        <ListItem v-for="item in group.items"
                  :key="item.uid"
                  :actions="actions"
                  :show-select="showSelect"
                  @action="(action) => emitAction(action, item.uid)"
                  @click="$emit('item-click', item.uid)">
          <slot v-bind="item"/>
        </ListItem>
      </template>

      <InfiniteScroll v-if="infiniteScroll"
                      :is-empty="false"
                      :identifier="refreshId"
                      :no-result-txt="emptyStateTitle"
                      @on-scroll="loadMore"/>
    </div>

  </div>
</template>

<script>
import ListItem from "@/modules/common/components/list/listItem/ListItem";
import GroupHeader from "@/modules/common/components/list/groupHeader/GroupHeader";
import {InfiniteScroll, NoResultsCmp} from "@/modules/common/components";
import EmptyState from "../../EmptyState/EmptyState";

export default {
  name: "EntityList",
  components: {ListItem, GroupHeader, InfiniteScroll, EmptyState, NoResultsCmp},
  props: {
    items: {
      type: Array,
      required: true
    },
    actions: {
      type: Array,
      required: false,
      default: () => []
    },
    showSelect: {
      type: Boolean,
      default: false
    },
    pageGetter: {
      type: String,
      required: true
    },
    completedGetter: {
      type: String,
      required: true
    },
    errorGetter: {
      type: String,
      required: true
    },
    pageSetter: {
      type: String,
      required: true
    },
    loadAction: {
      type: String,
      required: true
    },
    resetAction: {
      type: String,
      required: true
    },
    refreshId: {
      type: Number,
      required: true
    },
    emptyStateSvgPath: {
      type: String,
      required: true
    },
    emptyStateTitle: {
      type: String,
      required: true
    },
    emptyStateSubtitle: {
      type: String,
      required: true
    },
    emptyStateCtaText: {
      type: String,
      required: true
    },
    errorText: {
      type: String,
      required: true
    },
    errorActionText: {
      type: String,
      required: true
    },
    infiniteScroll: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    parsedGroups() {
      if (this.items[0] && this.items[0].items) {
        return this.items;
      }

      return [{items: this.items}];
    },
    isCompleted() {
      return this.$store.getters[this.completedGetter];
    },
    isError() {
      return this.$store.getters[this.errorGetter];
    },
    isEmpty() {
      return this.isCompleted && !this.isError && !this.items.length;
    },
    isLoading() {
      return !this.items.length && !this.isCompleted;
    }
  },
  methods: {
    emitAction(action, uid) {
      this.$emit('action', action.action, uid);
    },
    async loadMore($state) {
      const page = this.$store.getters[this.pageGetter];

      this.$store.commit(this.pageSetter, page + 1);
      await this.$store.dispatch({type: this.loadAction});

      if (this.isCompleted) {
        $state.complete();
        return;
      }

      $state.loaded();
    },
    refresh() {
      this.$store.dispatch({type: this.resetAction});
      this.$emit('refresh');
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/abstract/_variables.scss";

.entity-list {
  height: 100%;
  overflow: hidden;

  &--padding {
    padding-top: 30px;
  }

  .groups-container {
    height: 100%;
    overflow-y: auto;

    .list-item {
      border-bottom: 1px solid $grey-color-2;
      transition: background-color 300ms;
      cursor: pointer;

      &:hover {
        background-color: $grey-color-10;
      }
    }
  }

  .empty-state {
    width: 100%;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }

  .v-progress-circular {
    width: 100% !important;
  }
}
</style>
